<script lang="ts">
import { defineComponent, ref, reactive, onMounted } from 'vue'
import { documentsPath } from '@/generated/railsRoutes'
import LayoutSolutionContentsCards from '@/components/solutions/LayoutSolutionContentsCards.vue'
import SearchForm from '@/components/search/SearchForm.vue'
import DocumentsFilter from '@/components/documents/DocumentsFilter.vue'
import PaginationDefault from '@/components/pagination/PaginationDefault.vue'
import stateInitializer from '@/models/stateInitializer'
import paginationModel from '@/models/pagination'
import { useScreenLockStore } from '@/stores/screenLock'
import wait from '@/utilities/wait'

const PAGINATION_QUERY = 'page'

export default defineComponent({
  name: 'DocumentsMain',

  components: {
    LayoutSolutionContentsCards,
    SearchForm,
    DocumentsFilter,
    PaginationDefault,
  },

  setup() {
    interface State {
      search: {
        form: Form,
        input: {
          value: string,
          name: string,
        },
      },
      documentFilter: {
        form: Form,
        input: {
          solutionCategoryIds: {
            name: string,
            value: number[],
          },
          documentCategoryIds: {
            name: string,
            value: string[],
          },
        },
      }
      documents: SolutionDocumentItem[],
      pagination: Pagination,
    }
    const state: State = reactive({
      search: {
        form: {
          action: '',
          method: 'post',
        },
        input: {
          value: '',
          name: '',
        },
      },
      documentFilter: {
        form: {
          action: '',
          method: 'post',
        },
        input: {
          solutionCategoryIds: {
            name: '',
            value: [],
          },
          documentCategoryIds: {
            name: '',
            value: [],
          },
        },
      },
      documents: [],
      pagination: {
        page: 0,
        per: 0,
        totalCount: 0,
      },
    })
    stateInitializer(state, 'main')
    const { pagination } = paginationModel(state.pagination)

    // store
    const screenLock = useScreenLockStore()

    // 検索ワードの退避用
    const searchWord = ref('')

    onMounted(() => {
      // 検索ワードを別の箱に逃がす（ページ表示時に初期値を逃さないと、別のワードで検索欄に再入力している最中にリアルタイムでハイライトが切り替わってしまうため）
      searchWord.value = state.search.input.value
    })

    /**
     * 絞り込み遷移表示
     * @param solutionIds 絞り込みモーダルから emit で送られる製品カテゴリ ID 配列
     * @param documentIds 絞り込みモーダルから emit で送られる資料カテゴリ ID 配列
     * @param url 絞り込みモーダルから emit で送られる遷移 URL
     */
    const navigateToFilteredIndex = async (solutionIds: number[], documentIds: string[], url: string): void => {
      // 絞り込みモーダル自体が消える際の screenLock 解除のため、一瞬待機させる
      await wait(0.01)

      screenLock.lockScreen({
        hasBlackOut: false,
        hasBlur: true,
        hasWaitAnimation: true,
        hasScrollLock: true,
      })

      state.documentFilter.input.solutionCategoryIds.value = solutionIds
      state.documentFilter.input.documentCategoryIds.value = documentIds
      location.assign(url)
    }

    return {
      // constants
      PAGINATION_QUERY,

      // data
      state,
      searchWord,

      // computed
      pagination,

      // methods
      documentsPath,
      navigateToFilteredIndex,
    }
  },
})
</script>

<template>
  <section class="section">
    <div
      class="l-unit__documentIndex"
      :data-total-page="pagination.totalPage">
      <div class="l-unit__documentIndex--description">
        <LayoutSolutionContentsCards
          :items="state.documents"
          :highlight-word="searchWord" />
      <!-- /.l-unit__documentIndex description --></div>

      <!-- 検索フォーム -->
      <div class="l-unit__documentIndex--action is-searchForm">
        <div class="o-list__record">
          <dl class="o-list__record--unit">
            <dt class="o-list__record--term u-media__gtSP">
              ワードで探す
            </dt>

            <dd class="o-list__record--description">
              <SearchForm
                v-model="state.search.input.value"
                :form="state.search.form"
                :name="state.search.input.name"
                placeholder="例：中途採用"
                :has-reset="true">
                <template
                  v-for="categoryId in state.documentFilter.input.solutionCategoryIds.value"
                  :key="categoryId">
                  <input
                    class="input"
                    type="hidden"
                    :name="`${state.documentFilter.input.solutionCategoryIds.name}[]`"
                    :value="categoryId">
                </template>
                <template
                  v-for="categoryId in state.documentFilter.input.documentCategoryIds.value"
                  :key="categoryId">
                  <input
                    class="input"
                    type="hidden"
                    :name="`${state.documentFilter.input.documentCategoryIds.name}[]`"
                    :value="categoryId">
                </template>
              </SearchForm>
            </dd>
          </dl>
        <!-- /.o-list__record --></div>
      <!-- /.l-unit__documentIndex action --></div>

      <!-- カテゴリー選択 -->
      <div class="l-unit__documentIndex--action is-categorySelector">
        <DocumentsFilter
          :input-solution-category-ids="state.documentFilter.input.solutionCategoryIds"
          :input-document-category-ids="state.documentFilter.input.documentCategoryIds"
          :form="state.documentFilter.form"
          :total-count="pagination.totalCount"
          :page-query="PAGINATION_QUERY"
          @update:selected-category-ids="navigateToFilteredIndex" />
      <!-- /.l-unit__documentIndex action --></div>

      <!-- ページネーション -->
      <div
        v-if="pagination.totalPage"
        class="l-unit__documentIndex--action is-pagination">
        <PaginationDefault
          :page="pagination.page"
          :offset="4"
          :total-page="pagination.totalPage || 0" />
      <!-- /.l-unit__documentIndex action --></div>
    <!-- /.l-unit__documentIndex --></div>
  </section>
</template>

<style lang="sass" scoped>
// layout
.l-unit__documentIndex
  $root: &
  display: grid
  grid-template:
    rows: auto 1fr auto
    columns: auto 1fr

  & &--description
    width: 100%
    grid:
      row: 2 / 3
      column: 1 / 3
    margin-top: 1.5em

  $search-form-width: 380
  & &--action
    &.is-searchForm
      width: #{$search-form-width}px
      grid:
        row: 1 / 2
        column: 1 / 2

    &.is-categorySelector
      max-width: #{$global-contents-width-pc - $search-form-width}px
      grid:
        row: 1 / 2
        column: 2 / 3
      padding-left: 1.5em

    &.is-pagination
      width: 100%
      grid:
        row: 3 / 4
        column: 1 / 3
      margin-top: 3em

  +breakpoint
    display: flex
    flex-direction: column

    #{$root}--description
      margin-top: 12px

    #{$root}--action
      &.is-searchForm,
      &.is-categorySelector
        width: auto
        order: -1

      &.is-categorySelector
        max-width: none
        margin-top: 1em
        padding-left: 0

      &.is-pagination
        margin-top: 0
        padding-top: 12px
        border-top: 1px solid $border-x-light-gray

    &[data-total-page="1"]
      #{$root}--action
        &.is-pagination
          padding-top: 0
          border-top: none
</style>

